import React, { ReactNode } from 'react';
import i18next from 'i18next';
import dayjs from 'dayjs';
import { BillingPeriod } from '../../../constants/BillingPeriod';
import { EmbeddedNotificationWrapper } from '../EmbeddedNotificationWrapper/EmbeddedNotificationWrapper';
import { EmbeddedNotificationType } from '@signaturit/react-components-library';
import { UserFixedNotificationsFlow } from '../../../constants/UserFixedNotificationsFlow';
import { DowngradeDetails, IDowngradeDetailsFeature } from '../../../models/DowngradeDetails';
import { Features } from '../../../constants/Features';
import { SUBSCRIPTION_BILLING_URL } from '../../../config/routes';
import { Link } from '@material-ui/core';
import { ICancellationDetails } from '../../../models/CancellationDetails';
import { Trans } from 'react-i18next';

const DATE_FORMAT = 'DD/MM/YYYY';

type LoseFeaturesTextType = {
    locoKey: string;
    locoProps?: Function;
};

const loseFeaturesText = new Map<Features, LoseFeaturesTextType>([
    [
        Features.Template,
        {
            locoKey: 'default._notifications.downgrade.lose_feature_template',
            locoProps: (quantity = 0) => ({ templates: quantity }),
        },
    ],
    [
        Features.DcfSpot,
        {
            locoKey: 'default._notifications.downgrade.lose_feature_dcf',
        },
    ],
    [
        Features.Zapier,
        {
            locoKey: 'default._notifications.downgrade.lose_feature_zapier',
        },
    ],
]);

export const getDeveloperAccountNotificationTemplate = (className: string): ReactNode => (
    <div className={className}>
        <EmbeddedNotificationWrapper
            title={i18next.t('default._notifications.this_is_developer_account')}
            body={i18next.t('default._notifications.this_is_developer_account_body')}
            type={EmbeddedNotificationType.information}
            name={UserFixedNotificationsFlow.developerAccount}
        />
    </div>
);

export const getDowngradeNotificationTemplate = (
    classes: {
        downgradeLoseFeatures: string;
        embeddedNotificationWrapper: string;
        bulletRow: string;
        downgradeLink: string;
        bullet: string;
    },
    { downgradeScheduledFor, targetLicenseName, importantFeaturesToLose }: DowngradeDetails
): ReactNode => {
    const body = (
        <div className={classes.downgradeLoseFeatures}>
            {importantFeaturesToLose?.map((feature: IDowngradeDetailsFeature) => {
                const loseFeatureText = loseFeaturesText.get(feature?.code);
                const props = loseFeatureText?.locoProps ? loseFeatureText?.locoProps(feature?.maxQuantity) : undefined;
                return (
                    loseFeatureText && (
                        <div key={feature?.code} className={classes.bulletRow}>
                            <span className={classes.bullet}>·</span>
                            <span>{i18next.t(loseFeatureText.locoKey, props)}</span>
                        </div>
                    )
                );
            })}
            <Link className={classes.downgradeLink} href={SUBSCRIPTION_BILLING_URL}>
                {i18next.t('default._notifications.go_to_billing')}
            </Link>
        </div>
    );

    return (
        <div className={classes.embeddedNotificationWrapper}>
            <EmbeddedNotificationWrapper
                title={i18next.t('default._notifications.your_downgrade_will_be_on', {
                    date: dayjs(downgradeScheduledFor).format(DATE_FORMAT),
                    license: targetLicenseName,
                })}
                body={body}
                type={EmbeddedNotificationType.warning}
                name={UserFixedNotificationsFlow.downgraded}
            />
        </div>
    );
};
export const getAccountDisabledNotificationTemplate = (className: string, billingData: any): ReactNode => {
    let period = i18next.t('default._notifications.monthly');
    let date = dayjs().format(DATE_FORMAT);

    if (billingData?.billing_info) {
        period =
            billingData?.billing_info.period === BillingPeriod.annual
                ? i18next.t('default._notifications.annual')
                : i18next.t('default._notifications.monthly');
        date = dayjs(billingData?.billing_info?.next_payment_date).format(DATE_FORMAT);
    }

    return (
        <div className={className}>
            <EmbeddedNotificationWrapper
                title={i18next.t('default._notifications.this_account_is_disabled', {
                    period,
                    date,
                })}
                body={i18next.t('default._notifications.this_account_is_disabled_body')}
                type={EmbeddedNotificationType.error}
                name={UserFixedNotificationsFlow.accountDisabled}
            />
        </div>
    );
};

export const getCancellationPendingNotificationTemplate = (
    classes: {
        embeddedNotificationWrapper: string;
        downgradeLink: string;
    },
    { cancellationScheduledFor }: ICancellationDetails
): ReactNode => {
    const SUBSCRIPTION_LINK = '#/subscription/billing';
    const body = (
        <Trans
            i18nKey={'default._notifications.cancellation.body'}
            components={[
                <Link
                    data-signa={`link-action-${UserFixedNotificationsFlow.cancellationPending}`}
                    className={classes.downgradeLink}
                    target="_self"
                    href={SUBSCRIPTION_LINK}
                />,
            ]}
        />
    );

    return (
        <div className={classes.embeddedNotificationWrapper}>
            <EmbeddedNotificationWrapper
                title={i18next.t('default._notifications.cancellation.title', {
                    date: dayjs(cancellationScheduledFor).format(DATE_FORMAT),
                })}
                body={body}
                type={EmbeddedNotificationType.warning}
                name={UserFixedNotificationsFlow.cancellationPending}
            />
        </div>
    );
};
